import type { BoardStatus } from '../../shared/board';
import { request } from './api';

export interface RephraseVariant {
  kind: 'text' | 'question';
  label: string;
  did: string;
  expected: string;
  guess: string;
  question: string;
}

export interface RephraseResult {
  verdict: 'clear' | 'unclear';
  why: string;
  variants: RephraseVariant[];
  model: string;
}

export interface BugChatTurn {
  role: 'user' | 'agent';
  text: string;
}

export type BugStatus = BoardStatus;
export type BugAttachmentKind = 'screenshot' | 'voice' | 'file' | 'page';

export const BUG_STATUS_LABELS: Record<BugStatus, string> = {
  backlog: 'бэклог',
  requirements: 'требования',
  validation: 'валидация',
  implementation: 'реализация',
  deploy: 'деплой',
  fixed: 'сделано',
  wont_fix: 'не будет сделано',
  duplicate: 'дубль',
  closed: 'закрыто',
};

export interface BugUpload {
  id: number;
  kind: BugAttachmentKind;
  fileName: string;
  mimeType: string;
  size: number;
  meta: Record<string, unknown> | null;
  uploadedBy: string | null;
  createdAt: string;
}

export interface BugReportListItem {
  id: number;
  /** Т15: number within its project (screens show «№number»); older servers — absent, the id is the number. */
  number?: number;
  project: string;
  /** `ux` — a suggestion taken from a UX session; null — an ordinary report. */
  kind?: string | null;
  status: BugStatus;
  description: string;
  pageUrl: string | null;
  reporter: string | null;
  appVersion: string | null;
  duplicateOf: number | null;
  /** The duplicate's number and project (Т15). */
  duplicateOfNumber?: number | null;
  duplicateOfProject?: string | null;
  createdAt: string;
  updatedAt: string;
  attachments: { screenshots: number; voices: number; files: number };
  /** Пов10: a confirmed «повтор» / «дополнение» link or a high suggestion (the list marks it with a badge). */
  repeat?: RepeatBadgeInfo;
  /** Пов5–Пов8: `back`, `unfinished`, `closed`, `stuck` — the signs the report has (the filter «Повторы» of the board). */
  signs?: string[];
}

export interface RepeatBadgeInfo {
  kind: 'repeat' | 'continues' | 'suggest';
  number: number;
  direction: 'earlier' | 'later';
}

export type LinkKind = 'repeat' | 'continues' | 'not_helped' | 'other';

/** Пов2: a suggestion always has its confidence and at least one reason. */
export interface RepeatSuggestion {
  id: number;
  number: number;
  confidence: 'high' | 'similar';
  reasons: string[];
  excerpt: string;
  status: BugStatus;
}

/** Пов9: a person's decision on a pair — who and when. */
export interface RepeatLink {
  id: number;
  number: number;
  direction: 'earlier' | 'later';
  kind: LinkKind;
  source: 'suggest' | 'human';
  reason: string | null;
  decidedBy: string;
  decidedAt: string;
}

/** Пов5–Пов8: signs of the card; every one is only shown, nothing changes a status. */
export interface RepeatFlagsView {
  afterFix?: { lagHours: number; kind: 'soon' | 'later'; number: number; build: string };
  unfinished?: { level: 'possible' | 'likely'; signs: string[] };
  closedNoProof?: string;
  stuckDays?: number;
}

export interface RepeatView {
  suggestions: RepeatSuggestion[];
  links: RepeatLink[];
  flags?: RepeatFlagsView;
}

/** Пов12: the summary by themes. */
export interface ThemesView {
  days: string[];
  rows: {
    key: number;
    title: string;
    numbers: number[];
    perDay: number[];
    total: number;
    rate: number;
    verdict: 'repeats' | 'stopped' | 'continues' | null;
  }[];
}

/** The values of the list filter «Повторы»: all, a link or a high suggestion, or one of the signs. */
export type RepeatMode = '' | 'repeat' | 'back' | 'unfinished' | 'closed' | 'stuck';

export type EpicLinkKind = 'child' | 'rel';

export interface Epic {
  id: number;
  title: string;
  description: string;
  owner: string;
  status: string;
  /** Ап2: fixed / wont_fix / closed in Delivery Done, null elsewhere. */
  resolution: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** «Входят»: closed of all (the epic's project). */
  progress: { done: number; total: number };
  related: number;
}

export interface EpicLink {
  epicId: number;
  reportId: number;
  reportNumber?: number;
  kind: EpicLinkKind;
  project: string;
  status: BugStatus;
  description: string;
  epicTitle: string;
  epicStatus: string;
}

export interface EpicDetail {
  epic: Epic;
  links: EpicLink[];
}

/** Кн1, Кн10: the card's view of the control — the same shape the MCP gives (`control`). */
export interface ReportControlView {
  hard: boolean;
  /** Пв7: who took the report with take_next and when (older services — not named). */
  taken?: { by: string | null; at: string } | null;
  steps: {
    stage: string;
    stageTitle: string;
    final: boolean;
    doNow: string[];
    next: { stage: string; title: string; checks: { key: string; label: string; done: boolean; example: string; withMove?: boolean }[] } | null;
  };
  refusals: { at: string; author: string | null; from: string | null; to: string; missing: string[]; message: string }[];
}

export interface BugNote {
  at: string;
  author: string | null;
  channel: 'mcp' | 'web';
  statusFrom: BugStatus | null;
  statusTo: BugStatus | null;
  text: string | null;
}

export interface BugReportDetail extends Omit<BugReportListItem, 'attachments'> {
  attachments: BugUpload[];
  notes: BugNote[];
  clientState: unknown;
  serverState: unknown;
}

export interface BugToken {
  id: number;
  name: string;
  /** Мд2: the right on each project the token reaches (older services — none, the one project). */
  grants?: { project: string; access: 'read' | 'write' }[];
  prefix: string;
  projectScope: string | null;
  createdBy: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** null — no expiry. */
  expiresAt: string | null;
  access: 'read' | 'write';
}

/** A project's own MCP in the tracker: `<tracker>/mcp/<project>` and the line an agent runs to connect. */
export interface McpEndpoint {
  url: string;
  serverName: string;
  connect: string;
}

/** A host registered in the service («Проекты» of the settings, a project that reads all). */
export interface RegistryProject {
  name: string;
  keyPrefix: string;
  readsAll: boolean;
  createdAt: string;
  revokedAt: string | null;
  reports: number;
  /** This host's own project: its key is changed only from the service CLI. */
  own: boolean;
  /** Its MCP in the tracker (older services — none). */
  mcp?: McpEndpoint;
  /** Мд4: the card — people with a role in the project, its owners (super admins), live tokens that reach it. */
  users?: number;
  /** Ра11: operators of other projects (a cross-cutting role). */
  operators?: number;
  owners?: string[];
  tokens?: number;
}

/** A fresh key — shown once — and the lines for the host's .env. */
export interface IssuedProjectKey {
  name: string;
  key: string;
  env: string[];
  projects: RegistryProject[];
  mcp?: McpEndpoint;
  /** Мд3: the owner made with the project; the password — once, null when the operator typed it. */
  owner?: { username: string; password: string | null };
}

/** A token issued for a project from «Проекты»: shown once, with the connection line. */
export interface IssuedProjectToken {
  project: string;
  token: string;
  mcp: McpEndpoint;
  /** Тк1: a line per project of the token. */
  mcps?: McpEndpoint[];
  projects: RegistryProject[];
}

/** A tracker site account («Пользователи», tracker.jaiora.me only). */
export interface TrackerUser {
  id: number;
  username: string;
  project: string;
  isSuper: boolean;
  operator: boolean;
  /** The caller is a super of this user's project: may change the user. */
  manageable: boolean;
  /** Тг2: the projects the person works in — the own first, then the ones an operator added (older services — not named). */
  projects?: string[];
  createdAt: string;
  /** The caller: not deleted or demoted from the screen. */
  self: boolean;
}

export interface TrackerUsers {
  users: TrackerUser[];
  /** Projects a user can be put in. */
  projects: string[];
  /** Тг2: every live project an operator may add to a person (older services — not named). */
  allProjects?: string[];
  /** Ра5: the working project of the list and whether the caller may add and change people there (older services — not named). */
  project?: string;
  canManage?: boolean;
}

/** Ра4: a project of a token and the right on it. */
export interface TokenGrant {
  project: string;
  access: 'read' | 'write';
}

/** Кк1–Кк2: one row of the key list — never the key itself; createdBy 'cli' is the server command, null — not recorded. */
export interface HostKeyRow {
  keyPrefix: string;
  createdAt: string | null;
  createdBy: string | null;
  state: 'works' | 'revoked';
  endedReason: 'replaced' | 'revoked' | null;
  endedAt: string | null;
  endedBy: string | null;
  current: boolean;
}

/** Ра3: the keys of the project as the tab «Ключ хоста» shows them — never a key itself. */
export interface HostKeyInfo {
  name: string;
  keyPrefix: string;
  revokedAt: string | null;
  readsAll: boolean;
  /** The caller's own project: a new key of it comes from here, its revoke does not. */
  own: boolean;
  serviceUrl: string;
  /** Only an operator renews and revokes. */
  canChange: boolean;
  /** Кк1: the current key first, then the replaced and revoked ones. */
  keys?: HostKeyRow[];
}

/** A generated password — shown once. */
export interface IssuedPassword extends TrackerUsers {
  username: string;
  password: string | null;
}

export type BugPromptKey = 'bug_interpret' | 'bug_rephrase' | 'ux_review';

export interface BugPrompt {
  key: BugPromptKey;
  title: string;
  /** What the prompt is for, in a sentence. */
  hint?: string;
  /** Contract with the service code: read-only here. */
  system: string;
  user: string;
  defaultUser: string;
  custom: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** One field of the project profile; `value` — what goes to the model, `defaultValue` — the code seed. */
export interface BugProfileField {
  value: string;
  defaultValue: string;
  custom: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  maxLength: number;
}

export type BugProfileKey = 'title' | 'context' | 'glossary' | 'agentProcess';

export interface BugProjectProfile {
  name: string;
  base: string;
  /** Seed in use; null — the neutral default from the label. */
  seed: string | null;
  title: BugProfileField;
  context: BugProfileField;
  glossary: BugProfileField;
  /** Пв4: how the agent works in the project; goes to the agent in MCP. */
  agentProcess?: BugProfileField;
}

export interface BugSettings {
  enabled: boolean;
  /** The project of this host (its key). */
  project: string;
  /** The project the profile, transcribe model and prompts belong to — the working one. */
  target?: string;
  /** The person's own project reads all: «Проекты» (host registration) is theirs. */
  canRegister?: boolean;
  profile?: BugProjectProfile;
  database: string | null;
  problem: string | null;
  counts: { project: string; status: BugStatus; count: number }[];
  transcribe: {
    model: string;
    custom: boolean;
    updatedBy: string | null;
    updatedAt: string | null;
    /** What may be picked (older services — none: the screen then shows the saved name alone). */
    models?: { id: string; label: string; hint: string }[];
    /** The saved name has left the list. */
    unknown?: boolean;
  };
  autoCloseDays: number;
  /** The service's own OpenAI key (voice, the agent chat, UX review). */
  model?: { available: boolean; chatModel: string | null; problem: string | null };
  prompts?: BugPrompt[];
  /** This project's MCP in the tracker (older services — none). */
  mcp?: McpEndpoint;
  /** Public address of the tracker site (Пт8); older services — none. */
  trackerUrl?: string;
}

export interface QuestionPlace {
  kind: string;
  anchors: string[];
  text: string;
}

export interface SavedQuestionInput {
  question: string;
  places: QuestionPlace[];
  route: string;
  pageUrl: string;
  answer: { found: boolean; answer: string; guess: string | null; proofs: { file: string; line: number; quote: string }[] };
}

export interface StoredQuestion {
  id: number;
  project: string;
  asker: string | null;
  question: string;
  pageUrl: string | null;
  route: string | null;
  places: QuestionPlace[];
  found: boolean;
  answer: string;
  guess: string | null;
  proofs: { file: string; line: number; quote: string }[];
  reportId: number | null;
  reportNumber?: number | null;
  createdAt: string;
}

export interface BugSettingsPatch {
  transcribeModel?: string | null;
  prompts?: Partial<Record<BugPromptKey, string | null>>;
  profile?: Partial<Record<BugProfileKey, string | null>>;
}

/** `GET <base>/bugs/api/config`. */
/** What the widget offers in a project: a key left out is still the host's to decide. */
export type ProjectFeatureSet = Record<string, boolean | 'default-on'>;
/** What the site's widget last reported of itself: its own flags and the hooks the site gave. */
export interface WidgetSeen {
  features: ProjectFeatureSet;
  hooks: { news: boolean; tracker: boolean };
  at: string;
}

export type TrackerVersionState = 'active' | 'deprecated' | 'doomed';
/** A release of the tracker (tracker_versions Вр1–Вр4); `projects` — whose sites last loaded it. */
export interface TrackerVersion {
  version: string;
  state: TrackerVersionState;
  releasedAt: string;
  latest: boolean;
  projects: string[];
}
/** One action over several versions (Вр7): what it touched and what it left, with the reason in words. */
export type TrackerVersionBatchAction = TrackerVersionState | 'delete';
export interface TrackerVersionBatch {
  versions: TrackerVersion[];
  applied: string[];
  skipped: { version: string; reason: string }[];
}
export interface ProjectVersion {
  chosen: string | null;
  loaded: { version: string; at: string; state: TrackerVersionState | 'deleted' | null } | null;
}

export interface BugConfig {
  project: string;
  user: string | { name?: string } | null;
  isSuper: boolean;
  version?: string;
  transcribe?: { available: boolean };
  interpret?: { available: boolean };
  uxReview?: { available: boolean };
  /** Upload cap, if the service tells it; the widget assumes 8 MB otherwise. */
  limits?: { maxBodyMb?: number };
  /** What the widget offers in this project, set in the tracker (widget_features). */
  features?: Record<string, boolean | 'default-on'>;
}

function rawBody(blob: Blob): RequestInit {
  return { method: 'POST', body: blob, headers: { 'Content-Type': blob.type || 'application/octet-stream' } };
}

export const bugApi = {
  upload: (kind: BugAttachmentKind, name: string, blob: Blob) =>
    request<{ upload: BugUpload }>(
      `/bug-reports/uploads?kind=${kind}&name=${encodeURIComponent(name)}`,
      rawBody(blob),
    ),
  discard: (id: number) => request<{ ok: boolean }>(`/bug-reports/uploads/${id}`, { method: 'DELETE' }),
  transcribe: (blob: Blob, name: string, durationSec: number) =>
    request<{ upload: BugUpload; text: string | null; model: string | null; error: string | null }>(
      `/bug-reports/transcribe?name=${encodeURIComponent(name)}&duration=${Math.round(durationSec)}`,
      rawBody(blob),
    ),
  /** The agent chat: how the model understood the scenario from the snapshot, and its reply; the service keeps nothing. */
  interpret: (body: {
    pageUrl: string;
    clientState: unknown;
    answers: Record<string, string>;
    scenarios: Record<string, string>[];
    active: number;
    chat: BugChatTurn[];
    wishes: string[];
  }) =>
    request<{
      reply: string;
      understanding: { did: string; expected: string; guess: string };
      wishes: string[];
      agreed: boolean;
      model: string;
    }>('/bug-reports/interpret', { method: 'POST', body: JSON.stringify(body) }),

  /** «Переформулировать»: text, shots and the cleaned snapshot go to the model; it returns variants, the person picks; nothing is kept. */
  rephrase: (body: {
    pageUrl: string;
    clientState: unknown;
    answers: Record<string, string>;
    scenarios: Record<string, string>[];
    active: number;
    shots: { label: string; data: string }[];
    rejected: string[];
    round: number;
  }) => request<RephraseResult>('/bug-reports/rephrase', { method: 'POST', body: JSON.stringify(body) }),

  submit: (body: { description: string; pageUrl: string; clientState: unknown; attachmentIds: number[]; questionId?: number }) =>
    request<{ report: { id: number; number?: number; project: string; status: BugStatus } }>('/bug-reports', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** An answered question goes to the tracker (any user); the list — super: all, anyone else: own (`mine`). */
  saveQuestion: (body: SavedQuestionInput) =>
    request<{ question: { id: number } }>('/questions', { method: 'POST', body: JSON.stringify(body) }),
  questions: (before?: number | null) =>
    request<{ questions: StoredQuestion[]; nextBefore: number | null; mine?: boolean }>(`/questions${before ? `?before=${before}` : ''}`),
  removeQuestion: (id: number) => request<{ ok: boolean }>(`/questions/${id}`, { method: 'DELETE' }),

  list: (params: { status?: string; q?: string; before?: number | null; limit?: number; repeats?: RepeatMode }) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.q) qs.set('q', params.q);
    if (params.before) qs.set('before', String(params.before));
    if (params.repeats) qs.set('repeats', params.repeats === 'repeat' ? '1' : params.repeats);
    return request<{ reports: BugReportListItem[]; nextBefore: number | null }>(`/bug-reports?${qs.toString()}`);
  },
  get: (id: number) => request<{ report: BugReportDetail }>(`/bug-reports/${id}`),
  /** `expectedStatus` — the status the person saw: someone else changed it meanwhile — 409 (Д5). */
  /** `duplicateNumber` — «дубль №» in `duplicateProject` (default — the report's own project), Т15. */
  update: (
    id: number,
    patch: { status?: BugStatus; note?: string; duplicateOf?: number; duplicateNumber?: number; duplicateProject?: string; expectedStatus?: BugStatus },
  ) =>
    request<{ report: BugReportDetail; /** Кн4: a gate that did not stop the move — the person is only warned. */ warnings?: string[] }>(
      `/bug-reports/${id}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),
  /** Пов10: suggestions of repeats for the card and the decisions made; `decide` and `forget` — only a person (Пов1). */
  themes: () => request<ThemesView>('/bug-reports/themes'),
  similar: (id: number) => request<RepeatView>(`/bug-reports/${id}/similar`),
  decide: (id: number, to: number, kind: LinkKind) => request<RepeatView>(`/bug-reports/${id}/links`, { method: 'PUT', body: JSON.stringify({ to, kind }) }),
  forget: (id: number, to: number) => request<RepeatView>(`/bug-reports/${id}/links/${to}`, { method: 'DELETE' }),
  /** Кн10: «Контроль агента» of a report's card — the stage, the checklist, the last refusals. */
  reportControl: (id: number) => request<{ control: ReportControlView }>(`/bug-reports/${id}/control`),
  epics: () => request<{ epics: Epic[] }>('/epics'),
  epic: (id: number) => request<EpicDetail>(`/epics/${id}`),
  createEpic: (body: { title: string; description?: string }) =>
    request<{ epic: Epic }>('/epics', { method: 'POST', body: JSON.stringify(body) }),
  /** `expectedStatus` — the status the person saw (Э3). */
  updateEpic: (id: number, patch: { title?: string; description?: string; status?: string; resolution?: string; expectedStatus?: string }) =>
    request<EpicDetail>(`/epics/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  removeEpic: (id: number) => request<{ ok: boolean }>(`/epics/${id}`, { method: 'DELETE' }),
  linkReport: (epicId: number, reportId: number, kind: EpicLinkKind) =>
    request<EpicDetail>(`/epics/${epicId}/links`, { method: 'POST', body: JSON.stringify({ reportId, kind }) }),
  /** «Репорт №» — a number in `reportProject` (default — the viewer's own project), Т15. */
  linkReportNumber: (epicId: number, reportNumber: number, kind: EpicLinkKind, reportProject?: string) =>
    request<EpicDetail>(`/epics/${epicId}/links`, { method: 'POST', body: JSON.stringify({ reportNumber, reportProject, kind }) }),
  unlinkReport: (epicId: number, reportId: number, kind: EpicLinkKind) =>
    request<EpicDetail>(`/epics/${epicId}/links/${reportId}/${kind}`, { method: 'DELETE' }),
  reportEpics: (reportId: number) => request<{ links: EpicLink[] }>(`/bug-reports/${reportId}/epics`),
  remove: (id: number) => request<{ ok: boolean }>(`/bug-reports/${id}`, { method: 'DELETE' }),

  /** A probe: its failure is «disabled», not an error for the host. */
  config: () => request<BugConfig>('/config', {}, true),
  reportState: (state: unknown) => request<{ ok: boolean }>('/widget-state', { method: 'POST', body: JSON.stringify(state) }, true),
  settings: () => request<{ settings: BugSettings }>('/settings/bug-reports'),
  saveSettings: (patch: BugSettingsPatch) => request<{ settings: BugSettings }>('/settings/bug-reports', { method: 'PUT', body: JSON.stringify(patch) }),
  /** Ра4: `reach` — the projects the person may put on a token (older services — not named: the project itself). */
  tokens: () => request<{ tokens: BugToken[]; reach?: string[] }>('/settings/bug-reports/tokens'),
  /** expiresInDays: 30, 90, 365 or 0 — no expiry; `grants` — the projects of the token with a right on each (Ра4). */
  issueToken: (name: string, options: { expiresInDays: number; access: 'read' | 'write'; grants?: TokenGrant[] }) =>
    request<{ token: string; connect?: string; connects?: McpEndpoint[]; tokens: BugToken[] }>('/settings/bug-reports/tokens', {
      method: 'POST',
      body: JSON.stringify({ name, ...options }),
    }),
  revokeToken: (id: number) => request<{ tokens: BugToken[] }>(`/settings/bug-reports/tokens/${id}`, { method: 'DELETE' }),
  /** Ра4: the set of projects of an existing token — only the ones in the person's reach are changed. */
  setTokenGrants: (id: number, grants: TokenGrant[]) =>
    request<{ tokens: BugToken[] }>(`/settings/bug-reports/tokens/${id}/grants`, { method: 'PUT', body: JSON.stringify({ grants }) }),
  /** Ра4: «Реестр проектов → Токены» — every token of every project (an operator). */
  registryTokens: () => request<{ tokens: BugToken[] }>('/settings/bug-reports/registry/tokens'),
  issueRegistryToken: (name: string, options: { expiresInDays: number; access: 'read' | 'write'; grants: TokenGrant[] }) =>
    request<{ token: string; connect?: string; connects?: McpEndpoint[]; tokens: BugToken[] }>('/settings/bug-reports/registry/tokens', {
      method: 'POST',
      body: JSON.stringify({ name, ...options }),
    }),
  setRegistryTokenGrants: (id: number, grants: TokenGrant[]) =>
    request<{ tokens: BugToken[] }>(`/settings/bug-reports/registry/tokens/${id}/grants`, { method: 'PUT', body: JSON.stringify({ grants }) }),
  revokeRegistryToken: (id: number) => request<{ tokens: BugToken[] }>(`/settings/bug-reports/registry/tokens/${id}`, { method: 'DELETE' }),
  /** Ра3: «Проект → Ключ хоста» — the working project's key: prefix and state; only an operator changes it. */
  hostKey: () => request<{ hostKey: HostKeyInfo }>('/settings/bug-reports/host-key'),
  rotateHostKey: () => request<{ key: string; env: string[]; hostKey: HostKeyInfo }>('/settings/bug-reports/host-key/rotate', { method: 'POST' }),
  revokeHostKey: () => request<{ hostKey: HostKeyInfo }>('/settings/bug-reports/host-key/revoke', { method: 'POST' }),

  registry: () => request<{ projects: RegistryProject[] }>('/settings/bug-reports/registry'),
  /** «Проекты» → «Возможности» (tracker site, a super of a project that reads all; widget_features). */
  projectFeatures: (name: string) =>
    request<{ features: ProjectFeatureSet; seen: WidgetSeen | null; version?: ProjectVersion; agentControl?: { hard: boolean } }>(`/tracker/projects/${encodeURIComponent(name)}/features`),
  trackerVersions: () => request<{ versions: TrackerVersion[] }>('/tracker/versions'),
  chooseTrackerVersion: (name: string, version: string | null) =>
    request<{ version: ProjectVersion }>(`/tracker/projects/${encodeURIComponent(name)}/version`, { method: 'PUT', body: JSON.stringify({ version }) }),
  markTrackerVersion: (version: string, state: TrackerVersionState) =>
    request<{ versions: TrackerVersion[] }>(`/tracker/versions/${encodeURIComponent(version)}/state`, { method: 'POST', body: JSON.stringify({ state }) }),
  batchTrackerVersions: (versions: string[], action: TrackerVersionBatchAction, confirmed?: boolean) =>
    request<TrackerVersionBatch>('/tracker/versions/batch', { method: 'POST', body: JSON.stringify({ versions, action, ...(confirmed ? { confirmed } : {}) }) }),
  deleteTrackerVersion: (version: string) =>
    request<{ versions: TrackerVersion[] }>(`/tracker/versions/${encodeURIComponent(version)}`, { method: 'DELETE' }),
  /** Кн4: the project's «Контроль агента» switch (a hard refusal of the gates for the agent's token). */
  saveAgentControl: (name: string, hard: boolean) =>
    request<{ agentControl: { hard: boolean } }>(`/tracker/projects/${encodeURIComponent(name)}/agent-control`, { method: 'PUT', body: JSON.stringify({ hard }) }),
  saveProjectFeatures: (name: string, features: Record<string, boolean | 'default-on' | null>) =>
    request<{ features: ProjectFeatureSet }>(`/tracker/projects/${encodeURIComponent(name)}/features`, { method: 'PUT', body: JSON.stringify({ features }) }),
  /** Ра5: `project` — the people of the working project (the tab of the project); without it — every project's (the registry). */
  trackerUsers: (scope: 'project' | 'all' = 'all') => request<TrackerUsers>(`/tracker/users${scope === 'project' ? '?scope=project' : ''}`),
  addTrackerUser: (input: { username: string; project: string; isSuper: boolean }, scope: 'project' | 'all' = 'all') =>
    request<IssuedPassword>(`/tracker/users${scope === 'project' ? '?scope=project' : ''}`, { method: 'POST', body: JSON.stringify(input) }),
  resetTrackerPassword: (id: number, scope: 'project' | 'all' = 'all') =>
    request<IssuedPassword>(`/tracker/users/${id}/password${scope === 'project' ? '?scope=project' : ''}`, { method: 'POST' }),
  setTrackerRole: (id: number, isSuper: boolean, scope: 'project' | 'all' = 'all') =>
    request<TrackerUsers>(`/tracker/users/${id}/role${scope === 'project' ? '?scope=project' : ''}`, { method: 'POST', body: JSON.stringify({ isSuper }) }),
  setTrackerUserProjects: (id: number, projects: string[], scope: 'project' | 'all' = 'all') =>
    request<TrackerUsers>(`/tracker/users/${id}/projects${scope === 'project' ? '?scope=project' : ''}`, { method: 'PUT', body: JSON.stringify({ projects }) }),
  setTrackerOperator: (id: number, operator: boolean, scope: 'project' | 'all' = 'all') =>
    request<TrackerUsers>(`/tracker/users/${id}/operator${scope === 'project' ? '?scope=project' : ''}`, { method: 'POST', body: JSON.stringify({ operator }) }),
  removeTrackerUser: (id: number, scope: 'project' | 'all' = 'all') =>
    request<TrackerUsers>(`/tracker/users/${id}${scope === 'project' ? '?scope=project' : ''}`, { method: 'DELETE' }),
  registerProject: (name: string, owner: { username: string; password?: string }) =>
    request<IssuedProjectKey>('/settings/bug-reports/registry', { method: 'POST', body: JSON.stringify({ name, owner }) }),
  rotateProject: (name: string) =>
    request<IssuedProjectKey>(`/settings/bug-reports/registry/${encodeURIComponent(name)}/rotate`, { method: 'POST' }),
  issueProjectToken: (
    project: string,
    input: { name: string; access: 'read' | 'write'; expiresInDays: number; grants?: { project: string; access: 'read' | 'write' }[] },
  ) =>
    request<IssuedProjectToken>(`/settings/bug-reports/registry/${encodeURIComponent(project)}/token`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeProject: (name: string) =>
    request<{ projects: RegistryProject[] }>(`/settings/bug-reports/registry/${encodeURIComponent(name)}/revoke`, { method: 'POST' }),
};

/** Т15: «№number» of a report; with `project` — for lists spanning several projects. */
export function reportLabel(report: { id: number; number?: number | null; project?: string }, withProject = false): string {
  const label = `№${report.number ?? report.id}`;
  return withProject && report.project ? `${report.project} ${label}` : label;
}
